import { z } from 'zod';
import { SEVERITY_ORDER, type Severity } from './nivision';

/**
 * Wave 2 settings vocabulary (D-120): segments, the five settings scopes, per-rule tri-state overrides,
 * the change list used by the audit, and the provenance labels shown next to every value.
 */

/** Industry presets. Mirrored by the `Segment` table (D-141: `labs` is upserted by the Isotest onboarding, ops/isotest-onboarding). */
export const SEGMENTS: readonly { id: string; he: string; en: string }[] = [
  { id: 'car_leasing', he: 'ליסינג רכב', en: 'Car leasing' },
  { id: 'labs', he: 'מעבדות', en: 'Laboratories' },
  { id: 'retail', he: 'קמעונאות מוצרי חשמל', en: 'Appliance retail' },
  { id: 'insurance', he: 'ביטוח', en: 'Insurance' },
];

/** Settings scopes, general → specific. A `parent` row is the default of the parent's children only. */
export const SETTINGS_SCOPES = ['global', 'segment', 'parent', 'client', 'site'] as const;
export type SettingsScopeName = (typeof SETTINGS_SCOPES)[number];
/** Where an effective value comes from: one of the scopes, or the code default. */
export type SettingSource = SettingsScopeName | 'default';

export type TriState = 'inherit' | 'on' | 'off';
export const TRI_STATES = ['inherit', 'on', 'off'] as const;

/** A rule param value. Booleans are stored as 0 / 1 (as watchConfig today). */
export type ParamValue = number | string | string[];

const PARAM_NUMBER_LIMIT = 100_000;
const PARAM_STRING_MAX = 100;
const PARAM_LIST_MAX = 200;

export const ParamValueSchema: z.ZodType<ParamValue> = z.union([
  z.number().finite().min(-PARAM_NUMBER_LIMIT).max(PARAM_NUMBER_LIMIT),
  z.string().max(PARAM_STRING_MAX),
  z.array(z.string().max(PARAM_STRING_MAX)).max(PARAM_LIST_MAX),
]);

/** What one scope stores for one rule. An absent key = inherit. */
export interface RuleOverride {
  enabled?: boolean;
  severity?: Severity;
  params?: Record<string, ParamValue>;
}
export type RuleOverrides = Record<string, RuleOverride>;

export type RulePatch = { on?: TriState; severity?: Severity | 'inherit'; config?: Record<string, ParamValue> | null };

export const RulePatchSchema: z.ZodType<RulePatch> = z
  .object({
    on: z.enum(TRI_STATES).optional(),
    severity: z.enum([...SEVERITY_ORDER, 'inherit']).optional(),
    config: z.record(z.string().min(1).max(PARAM_STRING_MAX), ParamValueSchema).nullable().optional(),
  })
  .strict();

const MAX_RULES_PER_PATCH = 100;

export const RulesPatchSchema: z.ZodType<Record<string, RulePatch>> = z
  .record(z.string().min(1).max(100), RulePatchSchema)
  .refine((o) => Object.keys(o).length <= MAX_RULES_PER_PATCH, { message: `at most ${MAX_RULES_PER_PATCH} rules` });
export type RulesPatch = Record<string, RulePatch>;

const isPlainObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const isSeverity = (v: unknown): v is Severity => typeof v === 'string' && (SEVERITY_ORDER as readonly string[]).includes(v);

/** Tolerant read of a stored `ruleOverrides` JSON: junk keys and values are dropped, null / non-object → {}. */
export function parseRuleOverrides(v: unknown): RuleOverrides {
  if (!isPlainObject(v)) return {};
  const out: RuleOverrides = {};
  for (const [ruleId, raw] of Object.entries(v)) {
    if (!ruleId || !isPlainObject(raw)) continue;
    const o: RuleOverride = {};
    if (typeof raw.enabled === 'boolean') o.enabled = raw.enabled;
    if (isSeverity(raw.severity)) o.severity = raw.severity;
    if (isPlainObject(raw.params)) {
      const params: Record<string, ParamValue> = {};
      for (const [k, pv] of Object.entries(raw.params)) {
        if (k && ParamValueSchema.safeParse(pv).success) params[k] = pv as ParamValue;
      }
      if (Object.keys(params).length) o.params = params;
    }
    if (Object.keys(o).length) out[ruleId] = o;
  }
  return out;
}

/**
 * Applies a patch to the stored overrides of one scope: `on` 'on' | 'off' sets `enabled`, 'inherit' removes it; `severity`
 * 'inherit' removes it; `config: null` removes the params, an object replaces them (an empty object removes them).
 * An entry with nothing left is removed.
 */
export function applyRulesPatch(stored: RuleOverrides, patch: RulesPatch): RuleOverrides {
  const out: RuleOverrides = {};
  for (const [ruleId, o] of Object.entries(stored)) out[ruleId] = { ...o, ...(o.params ? { params: { ...o.params } } : {}) };
  for (const [ruleId, p] of Object.entries(patch)) {
    const cur: RuleOverride = out[ruleId] ?? {};
    if (p.on === 'on') cur.enabled = true;
    else if (p.on === 'off') cur.enabled = false;
    else if (p.on === 'inherit') delete cur.enabled;
    if (p.severity === 'inherit') delete cur.severity;
    else if (p.severity !== undefined) cur.severity = p.severity;
    if (p.config === null) delete cur.params;
    else if (p.config !== undefined) {
      if (Object.keys(p.config).length) cur.params = { ...p.config };
      else delete cur.params;
    }
    if (Object.keys(cur).length) out[ruleId] = cur;
    else delete out[ruleId];
  }
  return out;
}

/** One changed field of one rule. `field`: 'enabled' | 'severity' | 'params.<key>'; an absent value is 'inherit'. */
export interface RuleChange {
  ruleId: string;
  field: string;
  from: unknown;
  to: unknown;
}

const sameValue = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);
const orInherit = (v: unknown): unknown => (v === undefined ? 'inherit' : v);

/** The per-field difference of two override maps, by ruleId, then enabled, severity, params.<key> (sorted). */
export function describeRuleChanges(before: RuleOverrides, after: RuleOverrides): RuleChange[] {
  const ids = [...new Set([...Object.keys(before), ...Object.keys(after)])].sort();
  const changes: RuleChange[] = [];
  for (const ruleId of ids) {
    const b = before[ruleId] ?? {};
    const a = after[ruleId] ?? {};
    if (!sameValue(b.enabled, a.enabled)) changes.push({ ruleId, field: 'enabled', from: orInherit(b.enabled), to: orInherit(a.enabled) });
    if (!sameValue(b.severity, a.severity)) changes.push({ ruleId, field: 'severity', from: orInherit(b.severity), to: orInherit(a.severity) });
    const keys = [...new Set([...Object.keys(b.params ?? {}), ...Object.keys(a.params ?? {})])].sort();
    for (const k of keys) {
      const from = b.params?.[k];
      const to = a.params?.[k];
      if (!sameValue(from, to)) changes.push({ ruleId, field: `params.${k}`, from: orInherit(from), to: orInherit(to) });
    }
  }
  return changes;
}

/** A tunable param shown in the UI (number / flag / select / agents). List params are API-only, never knobs. */
export interface RuleKnob {
  key: string;
  label: string;
  help?: string;
  kind: 'number' | 'flag' | 'select' | 'agents';
  min?: number;
  max?: number;
  step?: number;
  options?: { value: string; label: string }[];
}

/** The provenance label shown next to an effective value. */
export function sourceLabel(src: SettingSource, lang: 'he' | 'en', names?: { segment?: string | null; parent?: string | null }): string {
  const segment = names?.segment ?? null;
  const parent = names?.parent ?? null;
  if (lang === 'he') {
    switch (src) {
      case 'default':
        return 'ברירת מחדל';
      case 'global':
        return 'מההגדרות הכלליות';
      case 'segment':
        return segment ? `מהגדרות ענף ${segment}` : 'מהגדרות הענף';
      case 'parent':
        return parent ? `מהגדרות לקוח-העל ${parent}` : 'מהגדרות לקוח-העל';
      case 'client':
        return 'שונה ללקוח הזה';
      case 'site':
        return 'שונה לסניף הזה';
    }
  }
  switch (src) {
    case 'default':
      return 'Default';
    case 'global':
      return 'From the general settings';
    case 'segment':
      return segment ? `From the ${segment} segment settings` : 'From the segment settings';
    case 'parent':
      return parent ? `From ${parent}` : 'From the parent account';
    case 'client':
      return 'Changed for this client';
    case 'site':
      return 'Changed for this branch';
  }
}
